import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { formatPrice, SHIPPING_OPTIONS, CATEGORY_FIELDS } from "../utils/helpers";
import ChatModal from "./ChatModal";
import ImageCarousel from "./ImageCarousel";
import ProductQuestions from "./ProductQuestions";
import { StarDisplay } from "./StarRating";
import { productsAPI } from "../services/api";

function SellerRating({ sellerId }) {
  const { getSellerRating } = useApp();
  const rating = getSellerRating(sellerId);
  if (!rating) return null;
  return (
    <div style={{marginTop:2}}>
      <StarDisplay rating={rating.average} total={rating.total} size={13} />
    </div>
  );
}
export default function ProductCard({ product, showActions = false }) {
  const { currentUser, deleteProduct, getOrCreateChat, startCheckout, toggleFavorite, isFavorite, getProductReviews } = useApp();
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [showBuyConfirm, setShowBuyConfirm] = useState(false);
  const [cantidad, setCantidad] = useState(1);
  const [bought, setBought] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyerDni, setBuyerDni] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerZone, setBuyerZone] = useState("");
  const [buyerColor, setBuyerColor] = useState("");
  const [buyError, setBuyError] = useState("");
  const [buyStep, setBuyStep] = useState(1);
  const [scrolledDetail, setScrolledDetail] = useState(false);

  useEffect(() => { if (showDetail) setScrolledDetail(false); }, [showDetail]);

  const isOwner = currentUser?.id === product.sellerId;
  const fav = currentUser ? isFavorite(product.id) : false;
  const shippingLabel = SHIPPING_OPTIONS.find(o => o.value === product.shipping)?.label || "";
  const extraFieldsDef = CATEGORY_FIELDS[product.category] || [];
  const zoneCosts = product.shippingZoneCosts || {};
  const hasZoneCosts = product.shipping === "comprador" && Object.keys(zoneCosts).length > 0;
  const shippingCost = hasZoneCosts
    ? (buyerZone ? (zoneCosts[buyerZone] || 0) : null)
    : (product.shipping === "comprador" ? (product.shippingCost || 0) : 0);
  const totalPrice = shippingCost === null ? null : product.price * cantidad + shippingCost;
  const imgSrc = product.images?.[0]?.url || null;
  const colorStockMap = product.colorStock || {};
  const hasColorStock = Object.keys(colorStockMap).length > 0;
  const colorOptions = product.color ? product.color.split(", ").filter(Boolean) : [];
  const needsColorChoice = colorOptions.length > 1;
  const maxQty = hasColorStock && buyerColor ? (colorStockMap[buyerColor] || 0) : product.quantity;

  const handleReport = async () => {
    if (!currentUser) { navigate("/login"); return; }
    const reason = window.prompt("¿Por qué querés reportar esta publicación? (opcional)");
    if (reason === null) return; // cancelo
    try {
      await productsAPI.report(product.id, reason);
      alert("Gracias, los administradores lo van a revisar.");
    } catch(e) {
      alert("No se pudo reportar: " + e.message);
    }
  };

  const handleContact = async () => {
    if (!currentUser) { navigate("/login"); return; }
    const chat = await getOrCreateChat(product.id, currentUser.id);
    if (chat) setActiveChat(chat.id);
    setShowDetail(false);
  };

  const handleConfirmBuy = async () => {
    if (!currentUser) { navigate("/login"); return; }
    if (!buyerDni.trim() || !buyerPhone.trim()) { setBuyError("Completa tu DNI y celular para continuar."); return; }
    if (hasZoneCosts && !buyerZone) { setBuyError("Elegi tu zona para continuar."); return; }
    if (needsColorChoice && !buyerColor) { setBuyError("Elegi el color que queres."); return; }
    setBuyError("");
    setBuying(true);
    try {
      const initPoint = await startCheckout(product.id, currentUser.id, currentUser.name, buyerDni.trim(), buyerPhone.trim(), cantidad, "", "", buyerZone, buyerColor);
      window.location.href = initPoint;
    } catch(e) {
      setBuyError(e.message);
      setBuying(false);
    }
  };

  const openBuy = () => {
    if (!currentUser) { navigate("/login"); return; }
    setCantidad(1);
    setBuyerDni(currentUser.dni || "");
    setBuyerPhone(currentUser.phone || "");
    setBuyerZone("");
    setBuyerColor("");
    setBuyError("");
    setBuyStep(1);
    setShowBuyConfirm(true);
    setShowDetail(false);
  };

  return (
    <>
      <div style={{...s.card, opacity: product.sold ? 0.6 : 1, cursor:"pointer"}} onClick={() => setShowDetail(true)}>
        <div style={{position:"relative"}}>
          <ImageCarousel files={product.images} height={180} />
          {product.sold && <div style={s.soldBadge}>Agotado</div>}
          <div style={s.categoryBadge}>{product.category}</div>

          {/* Icono zoom */}
          <div style={s.zoomBtn}>
            🔍
          </div>

          {/* Corazon favorito */}
          {currentUser && !isOwner && (
            <button style={s.favBtn} onClick={e => { e.stopPropagation(); toggleFavorite(product.id); }}>
              {fav ? "❤️" : "🤍"}
            </button>
          )}
        </div>

        <div style={s.body}>
          <p style={s.title}>{product.title}</p>
          <p style={s.seller}>por {product.sellerName?.split(" ")[0]}</p>
          <SellerRating sellerId={product.sellerId} />
          <p style={s.desc}>{product.description}</p>
          <div style={s.meta}>
            <span style={s.metaTag}>🎨 {product.color}</span>
            <span style={s.metaTag}>📦 {product.quantity} en stock</span>
            {product.zone && <span style={s.metaTag}>📍 {product.zone}</span>}
            {product.shipping && <span style={{...s.metaTag,background:"#fff8e1",color:"#b7830a"}}>🚚 {shippingLabel}</span>}
            {product.isInternational && <span style={{...s.metaTag,background:"#e8f4fd",color:"#009ee3"}}>🌎 Internacional</span>}
          </div>
          <div style={s.priceRow}><span style={s.price}>{formatPrice(product.price)}</span></div>
          {bought && <p style={s.boughtMsg}>✅ Compra registrada! Vela en "Mis compras"</p>}
          <div style={s.actions} onClick={e => e.stopPropagation()}>
            {!isOwner && !product.sold && !bought && (
              <button style={s.btnBuy} onClick={openBuy}>🛒 Comprar</button>
            )}
            {showActions && isOwner && (
              <>
                <button style={s.btnEdit} onClick={() => navigate(`/edit-product/${product.id}`)}>Editar</button>
                <button style={s.btnDelete} onClick={async () => {
                  if (!window.confirm("Eliminar?")) return;
                  try { await deleteProduct(product.id); } catch(e) { alert(e.message); }
                }}>Eliminar</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal detalle */}
      {showDetail && (
        <div style={s.overlay} onClick={() => setShowDetail(false)}>
          <div style={s.detailModalWrap}>
            <div style={s.detailModal} onClick={e => e.stopPropagation()} onScroll={e => { if (e.currentTarget.scrollTop > 20) setScrolledDetail(true); }}>
              <button style={s.closeBtn} onClick={() => setShowDetail(false)}>✕</button>
              <div onClick={e => e.stopPropagation()}>
                <ImageCarousel files={product.images} height={300} borderRadius="20px 20px 0 0" />
              </div>
              <div style={s.detailBody}>
              <div style={s.detailHeader}>
                <div>
                  <p style={s.detailTitle}>{product.title}</p>
                  <p style={s.detailSeller}>por {product.sellerName?.split(" ")[0]}</p>
                </div>
                <p style={s.detailPrice}>{formatPrice(product.price)}</p>
              </div>
              <p style={s.detailDesc}>{product.description}</p>
              <div style={s.detailMeta}>
                <div style={s.detailMetaItem}><span>🎨</span><span>{product.color}</span></div>
                <div style={s.detailMetaItem}><span>📦</span><span>Stock: {product.quantity}</span></div>
                {product.zone && <div style={s.detailMetaItem}><span>📍</span><span>{product.zone}</span></div>}
                {product.shipping && <div style={s.detailMetaItem}><span>🚚</span><span>{shippingLabel}</span></div>}
                {product.isInternational && <div style={s.detailMetaItem}><span>🌎</span><span>Producto internacional</span></div>}
                {product.estimatedDelivery && <div style={s.detailMetaItem}><span>⏱️</span><span>Llega en: {product.estimatedDelivery}</span></div>}
              </div>
              {extraFieldsDef.length > 0 && product.extraFields && Object.keys(product.extraFields).some(k => product.extraFields[k]) && (
                <div style={s.extraBox}>
                  <p style={s.extraBoxTitle}>Detalles del producto</p>
                  <div style={s.extraGrid}>
                    {extraFieldsDef.map(field => {
                      const val = product.extraFields?.[field.key];
                      if (!val) return null;
                      return (
                        <div key={field.key} style={s.extraItem}>
                          <span style={s.extraItemLabel}>{field.label}</span>
                          <span style={s.extraItemValue}>{val}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <span style={s.mpTag}>💳 Mercado Pago</span>
              {currentUser && !isOwner && (
                <button style={{...s.favBtnLarge, background: fav?"#FEE":"#f5f5f5", color: fav?"#e24b4a":"#555"}} onClick={() => toggleFavorite(product.id)}>
                  {fav ? "❤️ Guardado en favoritos" : "🤍 Guardar en favoritos"}
                </button>
              )}
              {!isOwner && !product.sold && !bought && (
                <button style={{...s.btnBuyLarge, marginTop:10}} onClick={openBuy}>🛒 Comprar</button>
              )}

              {/* Calificaciones con comentario */}
              {getProductReviews(product.id).length > 0 && (
                <div style={s.reviewsWrap}>
                  <p style={s.reviewsTitle}>⭐ Calificaciones</p>
                  <div style={s.reviewsList}>
                    {getProductReviews(product.id).map(r => (
                      <div key={r.id} style={s.reviewItem}>
                        <div style={s.reviewHeader}>
                          <span style={s.reviewName}>{r.buyer_name}</span>
                          <StarDisplay rating={r.rating} size={12} />
                        </div>
                        {r.comment && <p style={s.reviewComment}>{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ProductQuestions productId={product.id} sellerId={product.sellerId} />

              {!isOwner && (
                <button style={s.btnReport} onClick={handleReport}>🚩 Reportar publicación</button>
              )}
              </div>
            </div>
            {!scrolledDetail && (
              <div style={s.scrollHint} className="scroll-hint-bounce"><span>⌄</span></div>
            )}
          </div>
        </div>
      )}

      {/* Modal confirmar compra */}
      {showBuyConfirm && (
        <div style={s.overlay} onClick={() => setShowBuyConfirm(false)}>
          <div style={s.confirmModal} onClick={e => e.stopPropagation()}>
            <p style={s.confirmTitle}>Confirmar compra</p>

            {/* Foto del producto */}
            {imgSrc && <img src={imgSrc} alt={product.title} style={s.confirmImg} />}

            <div style={s.confirmProduct}>
              <span style={{fontWeight:500,fontSize:15}}>{product.title}</span>
              <span style={{color:"#888",fontSize:13}}>{formatPrice(product.price)} c/u</span>
            </div>

            {buyStep === 1 && (
              <>
                {product.isInternational && (
                  <p style={s.estimatedDeliveryNote}>🌎 <strong>Producto internacional</strong> — viene del exterior.</p>
                )}
                {product.estimatedDelivery && (
                  <p style={s.estimatedDeliveryNote}>⏱️ Tiempo estimado de entrega: <strong>{product.estimatedDelivery}</strong></p>
                )}
                {needsColorChoice && (
                  <div style={s.cantidadBox}>
                    <p style={s.cantidadLabel}>¿Que color queres?</p>
                    <select style={{...s.buyerInput,width:"100%"}} value={buyerColor} onChange={e => { setBuyerColor(e.target.value); setCantidad(1); }}>
                      <option value="">Seleccionar color...</option>
                      {colorOptions.map(c => (
                        <option key={c} value={c} disabled={hasColorStock && (colorStockMap[c]||0) <= 0}>
                          {c}{hasColorStock ? ` — ${(colorStockMap[c]||0) > 0 ? `${colorStockMap[c]} disponibles` : "sin stock"}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {maxQty > 1 && (
                  <div style={s.cantidadBox}>
                    <p style={s.cantidadLabel}>Cuantas unidades?</p>
                    <div style={s.cantidadRow}>
                      <button style={s.cantidadBtn} onClick={() => setCantidad(c => Math.max(1, c-1))}>−</button>
                      <span style={s.cantidadNum}>{cantidad}</span>
                      <button style={s.cantidadBtn} onClick={() => setCantidad(c => Math.min(maxQty, c+1))}>+</button>
                      <span style={s.cantidadStock}>max {maxQty}</span>
                    </div>
                  </div>
                )}

                {hasZoneCosts && (
                  <div style={s.cantidadBox}>
                    <p style={s.cantidadLabel}>¿En que zona estas?</p>
                    <select style={{...s.buyerInput,width:"100%"}} value={buyerZone} onChange={e => setBuyerZone(e.target.value)}>
                      <option value="">Seleccionar zona...</option>
                      {Object.keys(zoneCosts).map(z => (
                        <option key={z} value={z}>{z} — {formatPrice(zoneCosts[z])}</option>
                      ))}
                    </select>
                  </div>
                )}

                {product.zone && (
                  <p style={s.estimatedDeliveryNote}>📍 El producto esta en <strong>{product.zone}</strong>{product.shipping === "no" ? " — se retira ahi en persona" : ""}.</p>
                )}
                {product.shipping === "no" && (
                  <p style={s.estimatedDeliveryNote}>📩 El vendedor se va a comunicar mediante la plataforma para pasarte los datos para retirar el producto.</p>
                )}
                <p style={s.estimatedDeliveryNote}>⚠️ La plataforma no se hace cargo del estado del producto. Si tenes dudas, consultale al vendedor en "Preguntas y respuestas" antes de comprar.</p>

                {shippingCost === null ? null : shippingCost > 0 ? (
                  <div style={s.totalBox}>
                    <span style={s.totalLabel}>Producto{cantidad > 1 ? ` (${cantidad} unidades)` : ""} + envio</span>
                    <span style={s.totalPrice}>{formatPrice(product.price * cantidad)} + {formatPrice(shippingCost)} = {formatPrice(totalPrice)}</span>
                  </div>
                ) : (
                  <div style={s.totalBox}>
                    <span style={s.totalLabel}>{cantidad > 1 ? `${cantidad} unidades` : "Total"}</span>
                    <span style={s.totalPrice}>{formatPrice(totalPrice)}</span>
                  </div>
                )}

                <div style={s.confirmBtns}>
                  <button style={s.btnCancel} onClick={() => setShowBuyConfirm(false)}>Cancelar</button>
                  <button style={{...s.btnConfirm, opacity: (shippingCost===null || (needsColorChoice && !buyerColor))?0.5:1}} onClick={() => setBuyStep(2)} disabled={shippingCost===null || (needsColorChoice && !buyerColor)}>Continuar</button>
                </div>
              </>
            )}

            {buyStep === 2 && (
              <>
                {shippingCost > 0 ? (
                  <div style={s.totalBox}>
                    <span style={s.totalLabel}>Producto + envio</span>
                    <span style={s.totalPrice}>{formatPrice(product.price * cantidad)} + {formatPrice(shippingCost)} = {formatPrice(totalPrice)}</span>
                  </div>
                ) : (
                  <div style={s.totalBox}>
                    <span style={s.totalLabel}>{cantidad > 1 ? `${cantidad} unidades` : "Total"}</span>
                    <span style={s.totalPrice}>{formatPrice(totalPrice)}</span>
                  </div>
                )}

                <div style={s.buyerForm}>
                  <p style={s.cantidadLabel}>Tus datos</p>
                  <input style={s.buyerInput} type="tel" inputMode="numeric" placeholder="DNI" value={buyerDni}
                    onChange={e => setBuyerDni(e.target.value.replace(/\D/g, ""))} />
                  <input style={s.buyerInput} type="tel" inputMode="numeric" placeholder="Celular" value={buyerPhone}
                    onChange={e => setBuyerPhone(e.target.value.replace(/\D/g, ""))} />
                  {buyError && <p style={s.buyErrorText}>{buyError}</p>}
                </div>

                <div style={s.mpBox}>
                  <span style={{fontSize:20}}>💳</span>
                  <div>
                    <p style={{fontWeight:600,fontSize:14,color:"#009ee3"}}>Mercado Pago</p>
                    <p style={{fontSize:12,color:"#888"}}>Unico medio de pago aceptado</p>
                  </div>
                </div>

                <div style={s.confirmInfo}>
                  <p style={s.confirmInfoTitle}>⭐ Importante</p>
                  <p style={s.confirmInfoText}>En la sección "Mis compras" vas a encontrar un código único. La persona que te entregue el pedido te lo solicitará para confirmar que lo recibiste correctamente.{product.shipping !== "no" ? " Una vez confirmada la compra, el vendedor se va a comunicar con vos por la plataforma para coordinar la entrega." : ""}</p>
                </div>

                <div style={s.confirmBtns}>
                  <button style={s.btnCancel} onClick={() => setBuyStep(1)}>Atras</button>
                  <button style={{...s.btnConfirm, opacity: buying?0.7:1}} onClick={handleConfirmBuy} disabled={buying}>
                    {buying ? "Procesando..." : "💳 Pagar con Mercado Pago"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeChat && <ChatModal chatId={activeChat} onClose={() => setActiveChat(null)} />}
    </>
  );
}

const s = {
  card:{background:"#fff",borderRadius:12,border:"0.5px solid #e0e0e0",overflow:"hidden",display:"flex",flexDirection:"column"},
  soldBadge:{position:"absolute",top:8,right:8,background:"#E24B4A",color:"#fff",fontSize:11,fontWeight:500,padding:"3px 8px",borderRadius:20,zIndex:3},
  categoryBadge:{position:"absolute",top:8,left:8,background:"rgba(29,158,117,0.85)",color:"#fff",fontSize:11,fontWeight:500,padding:"3px 8px",borderRadius:20,zIndex:3},
  zoomBtn:{position:"absolute",bottom:8,right:8,background:"rgba(0,0,0,0.45)",color:"#fff",width:30,height:30,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,cursor:"pointer",zIndex:3},
  favBtn:{position:"absolute",bottom:8,left:8,background:"rgba(255,255,255,0.9)",border:"none",width:32,height:32,borderRadius:"50%",fontSize:16,cursor:"pointer",zIndex:3,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 1px 4px rgba(0,0,0,0.15)"},
  body:{padding:"12px 14px",display:"flex",flexDirection:"column",gap:6,flex:1},
  title:{fontSize:15,fontWeight:500,color:"#111"},
  seller:{fontSize:12,color:"#888"},
  desc:{fontSize:13,color:"#555",lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"},
  meta:{display:"flex",gap:6,flexWrap:"wrap"},
  metaTag:{fontSize:11,background:"#f4f4f4",color:"#555",padding:"2px 8px",borderRadius:20},
  priceRow:{marginTop:4},
  price:{fontSize:18,fontWeight:600,color:"#1D9E75"},
  boughtMsg:{fontSize:12,color:"#1D9E75",background:"#E1F5EE",padding:"6px 10px",borderRadius:8,textAlign:"center"},
  actions:{display:"flex",gap:6,marginTop:8},
  btnBuy:{flex:1,background:"#009ee3",color:"#fff",border:"none",borderRadius:8,padding:"8px 0",fontSize:13,fontWeight:600,cursor:"pointer"},
  btnContact:{flex:1,background:"#1D9E75",color:"#fff",border:"none",borderRadius:8,padding:"8px 0",fontSize:13,fontWeight:500,cursor:"pointer"},
  btnEdit:{flex:1,background:"#f0f0f0",color:"#333",border:"none",borderRadius:8,padding:"8px 0",fontSize:13,cursor:"pointer"},
  btnDelete:{flex:1,background:"#FEE",color:"#c0392b",border:"none",borderRadius:8,padding:"8px 0",fontSize:13,cursor:"pointer"},
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999,padding:16},
  detailModalWrap:{width:"100%",maxWidth:560,position:"relative"},
  detailModal:{background:"#fff",borderRadius:20,width:"100%",maxWidth:560,maxHeight:"90vh",overflow:"auto",position:"relative"},
  closeBtn:{position:"absolute",top:14,right:14,background:"rgba(0,0,0,0.45)",color:"#fff",border:"none",borderRadius:"50%",width:32,height:32,fontSize:14,cursor:"pointer",zIndex:10},
  scrollHint:{position:"absolute",bottom:10,left:"50%",transform:"translateX(-50%)",background:"rgba(0,0,0,0.6)",color:"#fff",width:30,height:30,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,lineHeight:1,pointerEvents:"none",zIndex:20},
  detailBody:{padding:"20px 24px 28px"},
  detailHeader:{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12},
  detailTitle:{fontSize:20,fontWeight:600,color:"#111"},
  detailSeller:{fontSize:13,color:"#888",marginTop:2},
  detailPrice:{fontSize:24,fontWeight:700,color:"#1D9E75"},
  detailDesc:{fontSize:14,color:"#555",lineHeight:1.6,marginBottom:16},
  detailMeta:{display:"flex",flexWrap:"wrap",gap:8,marginBottom:14},
  detailMetaItem:{display:"flex",alignItems:"center",gap:6,background:"#f5f5f5",borderRadius:20,padding:"5px 12px",fontSize:13,color:"#444"},
  extraBox:{background:"#f8fffe",border:"1px solid #c9e8dc",borderRadius:10,padding:"14px 16px",marginBottom:16},
  extraBoxTitle:{fontWeight:600,color:"#0F6E56",fontSize:13,marginBottom:10},
  extraGrid:{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8},
  extraItem:{display:"flex",flexDirection:"column",gap:2},
  extraItemLabel:{fontSize:11,color:"#888",textTransform:"uppercase",fontWeight:600},
  extraItemValue:{fontSize:14,color:"#111",fontWeight:500},
  mpTag:{display:"inline-block",fontSize:12,background:"#e8f4fd",color:"#009ee3",padding:"4px 10px",borderRadius:20,marginBottom:12,fontWeight:500},
  reviewsWrap:{marginTop:20,paddingTop:16,borderTop:"0.5px solid #f0f0f0"},
  reviewsTitle:{fontSize:15,fontWeight:600,color:"#111",marginBottom:12},
  reviewsList:{display:"flex",flexDirection:"column",gap:10},
  reviewItem:{background:"#f9f9f9",borderRadius:10,padding:12},
  reviewHeader:{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4},
  reviewName:{fontSize:13,fontWeight:600,color:"#333"},
  reviewComment:{fontSize:13,color:"#555",margin:0,lineHeight:1.5},
  favBtnLarge:{width:"100%",border:"0.5px solid #ddd",borderRadius:10,padding:"11px 0",fontSize:14,fontWeight:500,cursor:"pointer",marginBottom:8},
  btnBuyLarge:{width:"100%",background:"#009ee3",color:"#fff",border:"none",borderRadius:10,padding:"14px 0",fontSize:15,fontWeight:600,cursor:"pointer"},
  btnContactLarge:{width:"100%",background:"#1D9E75",color:"#fff",border:"none",borderRadius:10,padding:"14px 0",fontSize:15,fontWeight:600,cursor:"pointer"},
  confirmModal:{background:"#fff",borderRadius:16,padding:24,width:"100%",maxWidth:420,maxHeight:"90vh",overflowY:"auto"},
  confirmTitle:{fontSize:18,fontWeight:600,marginBottom:16},
  confirmImg:{width:"100%",height:160,objectFit:"cover",borderRadius:10,marginBottom:14},
  confirmProduct:{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#f5f5f5",borderRadius:10,padding:"12px 16px",marginBottom:16},
  cantidadBox:{background:"#f9f9f9",borderRadius:10,padding:"14px 16px",marginBottom:16},
  cantidadLabel:{fontSize:13,color:"#555",fontWeight:500,marginBottom:10},
  cantidadRow:{display:"flex",alignItems:"center",gap:12},
  cantidadBtn:{width:36,height:36,borderRadius:"50%",border:"0.5px solid #ddd",background:"#fff",fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"},
  cantidadNum:{fontSize:22,fontWeight:700,color:"#111",minWidth:30,textAlign:"center"},
  cantidadStock:{fontSize:12,color:"#aaa"},
  estimatedDeliveryNote:{fontSize:13,color:"#b7830a",background:"#fff8e1",borderRadius:8,padding:"10px 12px",marginBottom:14},
  btnReport:{marginTop:16,background:"none",border:"none",color:"#aaa",fontSize:12,cursor:"pointer",textDecoration:"underline",padding:0},
  totalBox:{display:"flex",justifyContent:"space-between",alignItems:"center",background:"#E1F5EE",borderRadius:10,padding:"14px 16px",marginBottom:16},
  totalLabel:{fontSize:14,color:"#555",fontWeight:500},
  totalPrice:{fontSize:22,fontWeight:700,color:"#1D9E75"},
  buyerForm:{background:"#f9f9f9",borderRadius:10,padding:"14px 16px",marginBottom:16,display:"flex",flexDirection:"column",gap:8},
  buyerInput:{border:"0.5px solid #ddd",borderRadius:8,padding:"10px 12px",fontSize:14,background:"#fff"},
  buyErrorText:{color:"#E24B4A",fontSize:13,margin:0},
  mpBox:{display:"flex",alignItems:"center",gap:12,background:"#e8f4fd",borderRadius:10,padding:"12px 16px",marginBottom:16},
  confirmInfo:{background:"#f0faf6",border:"0.5px solid #b0dcc9",borderRadius:10,padding:"14px",marginBottom:16},
  confirmInfoTitle:{fontSize:14,fontWeight:600,color:"#0F6E56",marginBottom:6},
  confirmInfoText:{fontSize:13,color:"#555",lineHeight:1.5},
  confirmBtns:{display:"flex",gap:10},
  btnCancel:{flex:1,padding:"11px 0",border:"0.5px solid #ddd",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:14,color:"#555"},
  btnConfirm:{flex:1,padding:"11px 0",background:"#009ee3",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:600,cursor:"pointer"},
};
